import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import { EnTetePage } from '../../commun/EnTetePage';
const roles: Record<string, string> = {
  instructeur_investissement: 'Instruction des investissements',
  decideur_investissement: 'Validation des investissements (avis et financement)',
};
const etats: Record<string, string> = {
  PENDING: 'À contrôler',
  APPROVED: 'Approuvée',
  REJECTED: 'Rejetée',
};
interface Reponse {
  peut_preparer: boolean;
  peut_controler: boolean;
  tronque: boolean;
  personnes: {
    id: string;
    compte_id: string;
    nom: string;
    mfa_actif: boolean;
    province: string | null;
    mandats: { code: string; statut: string }[];
  }[];
  demandes: {
    id: string;
    beneficiaire: string;
    beneficiaire_id: string;
    role: string;
    action: string;
    statut: string;
    motif: string;
    motif_controle: string | null;
    demande_par: string;
    cree_le: string;
  }[];
}
export function PageMandats() {
  const { contexte } = useSession();
  const client = useQueryClient();
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);
  const q = useQuery({
    queryKey: ['mandats-institutionnels', contexte?.appartenance_active?.id],
    queryFn: () => requete<Reponse>('/mandats-institutionnels'),
  });
  async function envoyer(chemin: string, corps: unknown) {
    setErreur('');
    setOccupe(true);
    try {
      await requete(chemin, { methode: 'POST', corps });
      await client.invalidateQueries({ queryKey: ['mandats-institutionnels'] });
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setOccupe(false);
    }
  }
  return (
    <section>
      <EnTetePage titre="Mandats institutionnels" />
      <p>
        Attribution et révocation des mandats d’investissement aux membres ministériels existants.
        Chaque demande exige le contrôle d’un autre compte, distinct du bénéficiaire. Les mandats
        d’administration ne sont pas attribuables ici.
      </p>
      {(erreur || q.error) && <p role="alert">{erreur || messageErreur(q.error)}</p>}
      {q.isPending && <p>Chargement des mandats…</p>}
      {q.data?.tronque && (
        <p role="status">Affichage limité aux 500 membres et 500 demandes les plus récentes.</p>
      )}
      {q.data?.peut_preparer && (
        <form
          style={{ display: 'grid', gap: 12, maxWidth: 750 }}
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            void envoyer('/mandats-institutionnels', {
              appartenance_id: f.get('appartenance'),
              role: f.get('role'),
              action: f.get('action'),
              motif: f.get('motif'),
            });
          }}
        >
          <h2>Préparer une demande</h2>
          <label>
            Bénéficiaire{' '}
            <select name="appartenance" required>
              <option value="">Choisir un membre</option>
              {q.data.personnes
                .filter((p) => p.compte_id !== contexte?.compte.id)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nom} · {p.province ?? 'National'}
                    {p.mfa_actif ? '' : ' · MFA à activer'}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Mandat{' '}
            <select name="role">
              {Object.entries(roles).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label>
            Action demandée{' '}
            <select name="action">
              <option value="attribuer">Attribuer le mandat</option>
              <option value="revoquer">Révoquer le mandat</option>
            </select>
          </label>
          <label>
            Motivation de la demande{' '}
            <textarea name="motif" required minLength={20} maxLength={2000} rows={3} />
          </label>
          <button disabled={occupe}>Soumettre au contrôle</button>
        </form>
      )}
      {q.data && (
        <details>
          <summary>Membres et mandats actuels</summary>
          <ul>
            {q.data.personnes.map((p) => (
              <li key={p.id}>
                {p.nom} · {p.province ?? 'National'} · MFA {p.mfa_actif ? 'actif' : 'à activer'}
                <ul>
                  {p.mandats.map((m) => (
                    <li key={m.code}>
                      {roles[m.code]} :{' '}
                      {m.statut === 'active'
                        ? 'Actif'
                        : m.statut === 'revoquee'
                          ? 'Révoqué'
                          : 'En attente'}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </details>
      )}
      <h2>Demandes et contrôles conservés</h2>
      {q.data?.demandes.length === 0 && <p>Aucune demande enregistrée.</p>}
      {q.data?.demandes.map((d) => (
        <article
          key={d.id}
          style={{ paddingBlock: 20, borderBottom: '1px solid var(--bordure, currentColor)' }}
        >
          <h3>
            {d.beneficiaire} · {roles[d.role]}
          </h3>
          <p>
            {d.action === 'attribuer' ? 'Attribution' : 'Révocation'} ·{' '}
            <strong>{etats[d.statut]}</strong> · {new Date(d.cree_le).toLocaleString('fr-FR')}
          </p>
          <p style={{ whiteSpace: 'pre-wrap' }}>{d.motif}</p>
          {d.motif_controle && <p>Contrôle : {d.motif_controle}</p>}
          {q.data.peut_controler &&
            d.statut === 'PENDING' &&
            d.demande_par !== contexte?.compte.id &&
            d.beneficiaire_id !== contexte?.compte.id && (
              <form
                style={{ display: 'grid', gap: 12, maxWidth: 750 }}
                onSubmit={(e) => {
                  e.preventDefault();
                  const f = new FormData(e.currentTarget);
                  void envoyer(`/mandats-institutionnels/${d.id}/controle`, {
                    decision: f.get('decision'),
                    motif: f.get('motif'),
                  });
                }}
              >
                <label>
                  Décision{' '}
                  <select name="decision">
                    <option value="approuver">Approuver</option>
                    <option value="rejeter">Rejeter</option>
                  </select>
                </label>
                <label>
                  Motivation du contrôle{' '}
                  <textarea name="motif" required minLength={20} maxLength={2000} rows={3} />
                </label>
                <button disabled={occupe}>Enregistrer le contrôle</button>
              </form>
            )}
        </article>
      ))}
    </section>
  );
}
